import GeoJSON from 'ol/format/GeoJSON.js';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef} from 'react';
import bbox from '@turf/bbox';

import GeojsonLayer from './GeojsonLayer.jsx';
import Map from './Map.jsx';

const FeaturesMap = ({
  allowInteractions = true,
  animationDuration = 800,
  children,
  features = [],
  featuresStyle,
  onFeatureClick,
  padding = [12, 12, 12, 12],
  selectedFeatures = [],
  zoomFeatures = [],
  ...props
}) => {
  const mapRef = useRef(null);
  const featureLayerRef = useRef(null);

  const zoomToExtent = useCallback(
    extent => {
      const mapView = mapRef.current.getView();

      mapView.fit(extent, {
        duration: animationDuration,
        padding,
      });
    },
    [animationDuration, padding],
  );

  const zoomToFeatures = useCallback(() => {
    if (features.length > 0) {
      const extent = bbox({
        type: 'FeatureCollection',
        features,
      });

      zoomToExtent(extent);
    }
  }, [features, zoomToExtent]);

  const onMapClick = event => {
    if (allowInteractions && onFeatureClick) {
      mapRef.current.forEachFeatureAtPixel(
        event.pixel,
        feature => {
          const geojson = new GeoJSON().writeFeatureObject(feature);
          onFeatureClick(geojson);
        },
        {
          layerFilter: layer => layer === featureLayerRef.current,
        },
      );
    }
  };

  useEffect(() => {
    if (mapRef?.current) {
      zoomToFeatures();
    }
  }, [mapRef, zoomToFeatures]);

  useEffect(() => {
    if (zoomFeatures.length > 0) {
      const extent = bbox({
        type: 'FeatureCollection',
        features: zoomFeatures,
      });

      zoomToExtent(extent);
    }
  }, [zoomToExtent, zoomFeatures]);

  return (
    <Map
      allowInteractions={allowInteractions}
      mapRef={mapRef}
      onSingleClick={onMapClick}
      onZoomToFeatures={zoomToFeatures}
      {...props}
    >
      <GeojsonLayer
        features={features}
        layerProps={{
          ref: featureLayerRef,
        }}
        style={featuresStyle}
      />
      <GeojsonLayer features={selectedFeatures} style="selectedPrimary" />
      {children}
    </Map>
  );
};

FeaturesMap.propTypes = {
  /**
   * If `true`, allow default map interactions
   */
  allowInteractions: PropTypes.bool,
  /**
   * Time in milliseconds for zoom in animation
   */
  animationDuration: PropTypes.number,
  /**
   * Additional map layers
   */
  children: PropTypes.node,
  /**
   * GeoJSON features to display
   */
  features: PropTypes.array,
  /**
   * The style to apply to the features layer
   */
  featuresStyle: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Callback fired after clicking a feature on the map.
   */
  onFeatureClick: PropTypes.func,
  /**
   * Padding to apply when zooming into features
   */
  padding: PropTypes.array,
  /**
   * Features to highlight as selected
   */
  selectedFeatures: PropTypes.array,

  /**
   * Features to zoom into and fit within the map view
   */
  zoomFeatures: PropTypes.array,
};

export default FeaturesMap;
